/**
 * Screen a whole cohort against one trial from a CSV of MRNs.
 *
 * Two steps, deliberately separate buttons rather than one. Ingestion brings each
 * patient's documents from FHIR and extracts their per-trial features; it is slow, costs
 * money, and is unnecessary for patients already ingested. Screening is what the CRC
 * actually wants and is cheap to retry. Fusing them would mean a failed screening
 * re-ingests everything, and would hide which half went wrong.
 */
import { useEffect, useState } from "react";
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { clinicalTrialsService } from "../api/clinicalTrialsService";
import { CohortResultPanel } from "../components/cohort-screening/CohortResultPanel";
import { JobDetails } from "../components/manual-summarization/JobDetails";
import { useManualJobHistory } from "../hooks/useManualJobHistory";
import {
  patientIngestErrorMessage,
  validatePatientCsv,
} from "../utils/patientCsvUpload";
import type {
  EligibilityTrialOption,
  ScreeningBatchResponse,
} from "../api/clinicalTrialsApiTypes";

/**
 * Cohort screening control panel.
 *
 * @returns The cohort screening page
 */
export function CohortScreeningPage() {
  const [file, setFile] = useState<File | null>(null);
  const [trials, setTrials] = useState<EligibilityTrialOption[]>([]);
  const [trialId, setTrialId] = useState("");
  const [ingesting, setIngesting] = useState(false);
  const [screening, setScreening] = useState(false);
  const [result, setResult] = useState<ScreeningBatchResponse | null>(null);
  const history = useManualJobHistory();

  useEffect(() => {
    let live = true;
    clinicalTrialsService
      .getEligibilityOptions()
      .then((options) => {
        if (!live) return;
        setTrials(options.trials);
        setTrialId((current) => current || options.trials[0]?.value || "");
      })
      .catch(() => {
        if (live) toast.error("Could not load the trial list", "Trials");
      });
    return () => {
      live = false;
    };
  }, []);

  const ingestJob = history.selectedJob;
  const ingestDone = ingestJob?.status === "success";
  const ingestRunning =
    ingestJob?.status === "queued" || ingestJob?.status === "running";

  const summary = file
    ? `${file.name} — ${(file.size / 1024 / 1024).toFixed(2)} MiB. Patient totals appear in the ingestion job.`
    : "Choose a CSV with an MRNS column. CSV files up to 20 MiB.";

  const handleFile = (chosen: File | undefined) => {
    setFile(null);
    setResult(null);
    if (!chosen) return;
    const error = validatePatientCsv(chosen);
    if (error) {
      toast.error(error, "Unusable CSV");
      return;
    }
    setFile(chosen);
  };

  const handleIngest = async () => {
    if (!file) {
      toast.error("Choose a CSV first", "Nothing to ingest");
      return;
    }
    setIngesting(true);
    try {
      const accepted = await clinicalTrialsService.uploadPatientCsv(file, {
        csv_format: "cohort_mrns",
      });
      void history.trackNewRun(accepted.run_id);
      toast.success(
        accepted.detail || "Bringing documents and extracting patient features",
        "Ingestion started",
      );
    } catch (err) {
      toast.error(patientIngestErrorMessage(err), "Error");
    } finally {
      setIngesting(false);
    }
  };

  const handleScreen = async () => {
    if (!file || !trialId) {
      toast.error("Choose a CSV and a trial first", "Nothing to screen");
      return;
    }
    setScreening(true);
    try {
      const queued = await clinicalTrialsService.startCohortScreening(
        trialId,
        file,
      );
      setResult(queued);
      toast.success(
        `${queued.queued} patient(s) queued against ${trialId}`,
        "Screening started",
      );
    } catch (err) {
      toast.error((err as Error)?.message || "Could not start", "Error");
    } finally {
      setScreening(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl p-8">
      <h1 className="text-2xl font-bold mb-1">Cohort Screening</h1>
      <p className="text-sm text-gray-500 mb-6">
        Upload a CSV of MRNs to screen a whole cohort against one trial. Results
        land on the eligibility worklist as each patient finishes.
      </p>

      <div className="rounded-lg border border-gray-200 p-6 mb-6 bg-white">
        <label htmlFor="cohort-csv" className="block text-sm font-medium mb-1">
          Cohort CSV
        </label>
        <input
          id="cohort-csv"
          type="file"
          disabled={ingesting || screening}
          accept=".csv,text/csv"
          onChange={(e) => void handleFile(e.target.files?.[0])}
          className="block text-sm"
        />
        <p className="text-xs text-gray-500 mt-1">{summary}</p>

        <div className="mt-4">
          <label className="block text-sm font-medium mb-1">Trial</label>
          <select
            value={trialId}
            onChange={(e) => setTrialId(e.target.value)}
            className="border border-gray-300 rounded-md p-2 text-sm min-w-[16rem]"
          >
            {trials.map((trial) => (
              <option key={trial.value} value={trial.value}>
                {trial.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-lg border border-gray-200 p-6 bg-white">
          <h3 className="font-semibold">1. Bring their data</h3>
          <p className="text-sm text-gray-500 mt-1 mb-4">
            Pulls 450 days of documents from FHIR, then extracts each
            patient&apos;s trial features. Skip this for patients already
            ingested.
          </p>
          <button
            onClick={() => void handleIngest()}
            disabled={ingesting || ingestRunning || !file}
            className="px-5 py-2.5 rounded-md text-sm font-semibold text-white"
            style={{
              backgroundColor: "var(--color-primary)",
              opacity: ingesting || ingestRunning || !file ? 0.6 : 1,
              cursor:
                ingesting || ingestRunning || !file ? "not-allowed" : "pointer",
            }}
          >
            {ingesting
              ? "Starting…"
              : ingestRunning
                ? "Ingesting…"
                : "Run Ingestion"}
          </button>
        </div>

        <div className="rounded-lg border border-gray-200 p-6 bg-white">
          <h3 className="font-semibold">2. Screen them</h3>
          <p className="text-sm text-gray-500 mt-1 mb-4">
            Queues one screening per patient. Patients with no worklist row get
            their display columns from FHIR first.
          </p>
          <button
            onClick={() => void handleScreen()}
            disabled={screening || !file || !trialId}
            className="px-5 py-2.5 rounded-md text-sm font-semibold text-white"
            style={{
              backgroundColor: "var(--color-primary)",
              opacity: screening || !file || !trialId ? 0.6 : 1,
              cursor:
                screening || !file || !trialId ? "not-allowed" : "pointer",
            }}
          >
            {screening ? "Queueing…" : "Start Screening"}
          </button>
          {ingestRunning && (
            <p className="text-xs text-gray-500 mt-2">
              Ingestion is still running. Screening now would run against
              whatever has landed so far.
            </p>
          )}
          {ingestDone && (
            <p className="text-xs text-gray-500 mt-2">
              Ingestion finished — this cohort is ready to screen.
            </p>
          )}
        </div>
      </div>

      {ingestJob && (
        <div className="mt-6">
          <JobDetails
            job={ingestJob}
            loading={history.detailLoading}
            error={history.detailError}
            resuming={history.resumingRunId === ingestJob.run_id}
            onResume={(runId) => void history.resumeRun(runId)}
          />
        </div>
      )}

      {result && (
        <div className="mt-6">
          <CohortResultPanel result={result} />
        </div>
      )}
    </div>
  );
}
